import type { SimpleFieldConfig } from "@/components/shared/UniversalCRMTable/types";
import { defineFieldConfigMap } from "@/components/shared/UniversalCRMTable/types/field-config-types";
import { COMPANY_STATUS_OPTIONS } from "../constants";
import { CONTRACT_TYPE_OPTIONS, PAYMENT_TERMS_OPTIONS } from "@/components/ui/selects/options/contact";

/**
 * Saved-view ids that include `website`/`last_contacted_at` (every status view, all-companies,
 * client + ticket-derived views). Used to keep per-field membership arrays terse.
 */
const STATUS_AND_DETAIL_VIEWS = [
  "all-companies",
  "status-active",
  "status-inactive",
  "status-prospect",
  "status-client",
  "status-former-client",
  "status-suspended",
  "clients-with-active-deals",
  "clients-without-active-deals",
  "under-contract",
  "no-contract",
  "contract-expiring-soon",
  "with-open-tickets",
  "cold-90-days",
] as const;

const ALL_LAST_CONTACTED_VIEWS = [
  "all-companies",
  "recent-companies",
  "status-active",
  "status-inactive",
  "status-prospect",
  "status-client",
  "status-former-client",
  "status-suspended",
  "clients-with-active-deals",
  "clients-without-active-deals",
  "no-tickets-this-month",
  "with-tickets-this-month",
  "no-tickets-last-month",
  "with-tickets-last-month",
  "under-contract",
  "no-contract",
  "contract-expiring-soon",
  "with-open-tickets",
  "cold-90-days",
] as const;

export const companyContactCommercialFields = defineFieldConfigMap({
    // ---------------------------------------------------------------------
    // SHARED COMPANY CONTACT METHODS
    //
    // These four columns are where a genuinely shared method lives — a front
    // desk, a switchboard, `info@`, `billing@`. Per CONTACT_METHOD_EXCLUSIVITY a
    // method identifies exactly ONE owner, so a number on the company must NOT
    // also sit on its people: they inherit its history through the company's
    // line anchor (`companies.line_contact_id`) instead of each carrying a copy.
    //
    // Without an editor here the only way to record a shared line was SQL, which
    // is why 178 numbers in production are still spread across the people who
    // answer them. Staff-only: which number is the front desk is internal
    // operational knowledge, and the aliases array is an admin-grade edit.
    // ---------------------------------------------------------------------
    // FIELD: organization_phone
    organization_phone: {
      // "text", not a dedicated phone type — the FieldType union has none, and
      // contacts.phone is typed the same way.
      type: "text",
      label: "Main Line",
      mobileDetailSectionId: "contact",
      sortable: true,
      filterable: true,
      searchable: true,
      resizable: true,
      hideable: true,
      editable: true,
      bulkEditable: false,
      filterType: "contains",
      width: 160,
      minWidth: 130,
      maxWidth: 220,
      placeholder: "Front desk / switchboard number",
      icon: "Phone",
      allowedRoles: ["wtc_employee", "wtc_admin"],
      graphqlSelector: "organization_phone",
    },
    // FIELD: organization_email
    organization_email: {
      type: "text",
      label: "Shared Mailbox",
      mobileDetailSectionId: "contact",
      sortable: true,
      filterable: true,
      searchable: true,
      resizable: true,
      hideable: true,
      editable: true,
      bulkEditable: false,
      filterType: "contains",
      width: 200,
      minWidth: 160,
      maxWidth: 300,
      placeholder: "info@ / billing@ — not a person's address",
      icon: "Mail",
      allowedRoles: ["wtc_employee", "wtc_admin"],
      graphqlSelector: "organization_email",
    },
    // FIELD: phone_aliases — additional shared lines (the array editor is
    // reused from contacts; the dispatcher keys on the field NAME, so no new
    // component is needed).
    phone_aliases: {
      type: "array",
      label: "Other Company Lines",
      mobileDetailSectionId: "contact",
      sortable: false,
      filterable: true,
      searchable: true,
      resizable: true,
      hideable: true,
      editable: true,
      bulkEditable: false,
      formSpan: 3,
      filterType: "contains",
      width: 250,
      minWidth: 200,
      maxWidth: 400,
      customComponent: "PhoneAliasesEditor",
      placeholder: "Add other shared numbers for this company",
      icon: "Phone",
      arrayConfig: {
        itemType: "phone",
        allowCustom: true,
        maxItems: 10,
      },
      allowedRoles: ["wtc_admin", "wtc_employee"],
      graphqlSelector: "phone_aliases",
    },
    // FIELD: email_aliases — additional shared mailboxes.
    email_aliases: {
      type: "array",
      label: "Other Shared Mailboxes",
      mobileDetailSectionId: "contact",
      sortable: false,
      filterable: true,
      searchable: true,
      resizable: true,
      hideable: true,
      editable: true,
      bulkEditable: false,
      formSpan: 3,
      filterType: "contains",
      width: 250,
      minWidth: 200,
      maxWidth: 400,
      customComponent: "EmailAliasesEditor",
      placeholder: "Add other shared addresses for this company",
      icon: "Mail",
      arrayConfig: {
        itemType: "email",
        allowCustom: true,
        maxItems: 10,
        validateItem: (item: string) => {
          const EMAIL_REGEX = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
          return EMAIL_REGEX.test(item) || "Invalid email format";
        },
      },
      allowedRoles: ["wtc_admin"],
      graphqlSelector: "email_aliases",
    },

    // FIELD: website
    website: {
      type: "text",
      label: "Website",
      mobileDetailSectionId: "contact",
      sortable: true,
      filterable: true,
      formSpan: 3,
      searchable: true,
      resizable: true,
      hideable: true,
      editable: true,
      filterType: "contains",
      width: 200,
      minWidth: 150,
      maxWidth: 300,
      placeholder: "https://example.com",
      icon: "Globe",
      // Role-based access control - visible to all roles
      allowedRoles: ["client_user", "wtc_employee", "wtc_admin"],
      showOnCard: true,
      cardSpan: 3,
      graphqlSelector: "website",
      savedViewMembership: STATUS_AND_DETAIL_VIEWS,
    },
    // FIELD: last_contacted_at
    // Moved here from metadata so iteration order produces canonical saved-view
    // column order (industry → website → last_contacted_at → commercial → dates).
    last_contacted_at: {
      type: "datetime",
      label: "Last activity",
      mobileDetailSectionId: "metadata",
      sortable: true,
      filterable: true,
      searchable: false,
      resizable: true,
      hideable: true,
      editable: false,
      isReadOnlyDerived: true,
      readOnlyReason:
        "Latest communication across any contact on this account (max communications.occurred_at).",
      dataLineage: {
        kind: "trigger-populated",
        sourceTable: "communications",
        sourceField: "occurred_at",
        notes: "Rolled up from contacts on this company; see migration company_last_contacted_at.",
      },
      width: 168,
      minWidth: 120,
      maxWidth: 220,
      icon: "MessageSquare",
      groupable: true,
      groupingOptions: {
        dateGranularity: "day",
      },
      datetimePresets: {
        enabled: true,
        presets: [
          "never",
          "today",
          "yesterday",
          "last7days",
          "last14days",
          "last30days",
          "thismonth",
          "lastmonth",
        ],
        showCustomRange: true,
      },
      engagementStaleness: true,
      // Internal/audit — staff only.
      allowedRoles: ["wtc_employee", "wtc_admin"],
      showOnCard: true,
      cardSpan: 1,
      graphqlSelector: "last_contacted_at",
      savedViewMembership: ALL_LAST_CONTACTED_VIEWS,
    },
    // FIELD: contract_type
    contract_type: {
      type: "select",
      label: "Contract Type",
      mobileDetailSectionId: "commercial",
      sortable: true,
      filterable: true,
      searchable: false, // Select fields typically not searchable
      resizable: true,
      hideable: true,
      editable: true,
      bulkEditable: true, // Enable bulk editing
      formSpan: 1,
      filterType: "exact",
      width: 130,
      minWidth: 100,
      maxWidth: 180,
      placeholder: "Select type",
      icon: "FileText",
      options: CONTRACT_TYPE_OPTIONS,
      groupable: true,
      // Role-based access control - admin only for contract details
      allowedRoles: ["wtc_admin"],
      graphqlSelector: "contract_type",
      savedViewMembership: ["all-companies"],
    },
    // FIELD: hourly_rate
    hourly_rate: {
      type: "text",
      label: "Hourly Rate",
      mobileDetailSectionId: "commercial",
      sortable: true,
      filterable: true,
      searchable: false, // Number fields typically not searchable
      resizable: true,
      hideable: true,
      editable: true,
      bulkEditable: true, // Enable bulk editing
      formSpan: 1,
      filterType: "range",
      width: 120,
      minWidth: 100,
      maxWidth: 150,
      placeholder: "0.00",
      icon: "DollarSign",
      groupable: true,
      // Role-based access control - admin only for financial details
      allowedRoles: ["wtc_admin"],
      graphqlSelector: "hourly_rate",
      savedViewMembership: ["all-companies"],
    },
    // FIELD: monthly_rate
    monthly_rate: {
      type: "text",
      label: "Monthly Rate",
      mobileDetailSectionId: "commercial",
      sortable: true,
      filterable: true,
      searchable: false, // Number fields typically not searchable
      resizable: true,
      hideable: true,
      editable: true,
      bulkEditable: true, // Enable bulk editing
      formSpan: 1,
      filterType: "range",
      width: 130,
      minWidth: 100,
      maxWidth: 160,
      placeholder: "0.00",
      icon: "DollarSign",
      groupable: true,
      // Internal/financial — staff only.
      allowedRoles: ["wtc_employee", "wtc_admin"],
      graphqlSelector: "monthly_rate",
      savedViewMembership: ["all-companies"],
    },
    // FIELD: payment_terms
    payment_terms: {
      type: "select",
      label: "Payment Terms",
      mobileDetailSectionId: "commercial",
      sortable: true,
      filterable: true,
      searchable: false, // Select fields typically not searchable
      resizable: true,
      hideable: true,
      editable: true,
      bulkEditable: true, // Enable bulk editing
      formSpan: 1,
      filterType: "exact",
      width: 140,
      minWidth: 120,
      maxWidth: 200,
      placeholder: "Select terms",
      icon: "CreditCard",
      options: PAYMENT_TERMS_OPTIONS,
      groupable: true,
      // Internal/financial — staff only.
      allowedRoles: ["wtc_employee", "wtc_admin"],
      graphqlSelector: "payment_terms",
    },
    // FIELD: tax_id
    tax_id: {
      type: "text",
      label: "Tax ID",
      mobileDetailSectionId: "contact",
      sortable: true,
      filterable: true,
      searchable: true,
      resizable: true,
      hideable: true,
      editable: true,
      formSpan: 1,
      filterType: "contains",
      width: 120,
      minWidth: 100,
      maxWidth: 150,
      placeholder: "XX-XXXXXXX",
      icon: "Hash",
      // Internal/financial — staff only.
      allowedRoles: ["wtc_employee", "wtc_admin"],
      graphqlSelector: "tax_id",
    },
    // FIELD: slack_channel_id
    // Target channel for this client's monthly billing/time-summary report
    // (hours only, no revenue). Staff-only internal routing config.
    slack_channel_id: {
      type: "text",
      label: "Slack Channel ID",
      mobileDetailSectionId: "contact",
      sortable: false,
      filterable: true,
      searchable: false,
      resizable: true,
      hideable: true,
      editable: true,
      formSpan: 1,
      filterType: "contains",
      width: 160,
      minWidth: 120,
      maxWidth: 220,
      placeholder: "C0123ABCDEF",
      icon: "MessageSquare",
      // Internal routing — not exposed to client portal users.
      allowedRoles: ["wtc_employee", "wtc_admin"],
      graphqlSelector: "slack_channel_id",
    }
});
